import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ST_COLORS, MONTHS_AR, fmt, stLabel, projLabel } from './constants';

// ── ReportsTab ──
// Two reports: Commission report (filterable by period) and Earnest Money report
// (all units that currently hold a deposit). Both come straight from the existing
// backend endpoints — this is a UI port, not a logic change.
function ReportsTab({ t, lang }) {
  const [reportType, setReportType] = useState('comm');
  const [period, setPeriod] = useState('monthly');
  const [month, setMonth] = useState(MONTHS_AR[new Date().getMonth()]);
  const [quarter, setQuarter] = useState('Q' + (Math.floor(new Date().getMonth() / 3) + 1));
  const [data, setData] = useState(null);
  const [earnest, setEarnest] = useState(null);

  useEffect(() => {
    const params = { period };
    if (period === 'monthly') params.month = month;
    if (period === 'quarterly') params.quarter = quarter;
    axios.get('/api/units-deals/reports/commissions', { params }).then((r) => setData(r.data)).catch(() => {});
    axios.get('/api/units-deals/reports/earnest').then((r) => setEarnest(r.data)).catch(() => {});
  }, [period, month, quarter]);

  return (
    <div>
      {/* Report type switch */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {[
          { k: 'comm', l: t('udCommissionReport') },
          { k: 'earn', l: t('udEarnestReport') },
        ].map((r) => (
          <button
            key={r.k}
            onClick={() => setReportType(r.k)}
            style={{
              padding: '7px 16px',
              borderRadius: 8,
              border: 'none',
              fontSize: '0.78rem',
              fontWeight: 700,
              cursor: 'pointer',
              background: reportType === r.k ? 'var(--accent)' : 'var(--bg-card)',
              color: reportType === r.k ? '#fff' : 'var(--text-secondary)',
              ...(reportType !== r.k ? { border: '1px solid var(--border)' } : {}),
            }}
          >
            {r.l}
          </button>
        ))}
      </div>

      {reportType === 'comm' && data && (
        <CommissionsReport
          data={data}
          period={period}
          setPeriod={setPeriod}
          month={month}
          setMonth={setMonth}
          quarter={quarter}
          setQuarter={setQuarter}
          t={t}
          lang={lang}
        />
      )}

      {reportType === 'earn' && earnest && <EarnestReport earnest={earnest} t={t} lang={lang} />}
    </div>
  );
}

function CommissionsReport({ data, period, setPeriod, month, setMonth, quarter, setQuarter, t, lang }) {
  const kpis = [
    { l: lang === 'ar' ? 'الصفقات' : 'Deals', v: data.deals, c: 'var(--accent)' },
    { l: lang === 'ar' ? 'قيمة المبيعات' : 'Sales Value', v: fmt(data.totalValue), c: '#c9a84c' },
    { l: lang === 'ar' ? 'عمولات الفريق' : 'Team Commissions', v: fmt(data.totalCommissions), c: '#3b82f6' },
    { l: lang === 'ar' ? 'عمولات الوسطاء' : 'Broker Commissions', v: fmt(data.totalBrokerComm), c: '#f59e0b' },
  ];

  return (
    <>
      {/* Period switcher */}
      <div
        style={{
          display: 'flex',
          gap: 4,
          background: 'var(--bg-card)',
          borderRadius: 8,
          padding: 3,
          marginBottom: 10,
          border: '1px solid var(--border)',
          maxWidth: 360,
        }}
      >
        {[
          { k: 'monthly', l: t('udMonthly') },
          { k: 'quarterly', l: t('udQuarterly') },
          { k: 'annual', l: t('udAnnual') },
        ].map((p) => (
          <button
            key={p.k}
            onClick={() => setPeriod(p.k)}
            style={{
              flex: 1,
              padding: 6,
              borderRadius: 6,
              border: 'none',
              fontSize: '0.74rem',
              fontWeight: 700,
              cursor: 'pointer',
              background: period === p.k ? 'var(--bg-primary)' : 'transparent',
              color: period === p.k ? 'var(--accent)' : 'var(--text-secondary)',
              boxShadow: period === p.k ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
            }}
          >
            {p.l}
          </button>
        ))}
      </div>

      {period === 'monthly' && (
        <select
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          style={{
            padding: '6px 10px',
            borderRadius: 8,
            border: '1px solid var(--border)',
            fontSize: '0.78rem',
            marginBottom: 14,
          }}
        >
          {MONTHS_AR.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      )}
      {period === 'quarterly' && (
        <select
          value={quarter}
          onChange={(e) => setQuarter(e.target.value)}
          style={{
            padding: '6px 10px',
            borderRadius: 8,
            border: '1px solid var(--border)',
            fontSize: '0.78rem',
            marginBottom: 14,
          }}
        >
          {['Q1', 'Q2', 'Q3', 'Q4'].map((q) => (
            <option key={q} value={q}>
              {q}
            </option>
          ))}
        </select>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 10,
          marginBottom: 16,
        }}
      >
        {kpis.map((k, i) => (
          <div key={i} className="card" style={{ padding: 14, textAlign: 'center' }}>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', marginBottom: 4 }}>{k.l}</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 800, color: k.c, direction: 'ltr' }}>{k.v}</div>
          </div>
        ))}
      </div>

      {data.team.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <h2>{lang === 'ar' ? 'أداء الفريق' : 'Team Performance'}</h2>
          </div>
          <div className="data-table-wrapper">
            <table className="data-table" style={{ fontSize: '0.78rem' }}>
              <thead>
                <tr>
                  <th>{lang === 'ar' ? 'الموظف' : 'Employee'}</th>
                  <th>{lang === 'ar' ? 'الصفقات' : 'Deals'}</th>
                  <th>{lang === 'ar' ? 'العمولة' : 'Commission'}</th>
                  <th>{lang === 'ar' ? 'الحافز' : 'Bonus'}</th>
                  <th>{lang === 'ar' ? 'الإجمالي' : 'Total'}</th>
                </tr>
              </thead>
              <tbody>
                {data.team.map((m, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 700 }}>{m.name}</td>
                    <td>{m.deals}</td>
                    <td style={{ direction: 'ltr' }}>{fmt(m.commission)}</td>
                    <td style={{ color: m.bonus > 0 ? '#22c55e' : 'var(--text-secondary)', direction: 'ltr' }}>
                      {fmt(m.bonus)}
                    </td>
                    <td style={{ fontWeight: 800, direction: 'ltr' }}>{fmt(m.commission + m.bonus)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data.brokers.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h2>{lang === 'ar' ? 'الوسطاء' : 'Brokers'}</h2>
          </div>
          <div className="data-table-wrapper">
            <table className="data-table" style={{ fontSize: '0.78rem' }}>
              <thead>
                <tr>
                  <th>{lang === 'ar' ? 'الوسيط' : 'Broker'}</th>
                  <th>{lang === 'ar' ? 'الصفقات' : 'Deals'}</th>
                  <th>{lang === 'ar' ? 'العمولة' : 'Commission'}</th>
                </tr>
              </thead>
              <tbody>
                {data.brokers.map((b, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 700 }}>{b.name}</td>
                    <td>{b.deals}</td>
                    <td style={{ direction: 'ltr', fontWeight: 700 }}>{fmt(b.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

function EarnestReport({ earnest, t, lang }) {
  return (
    <div className="card">
      <div className="card-header">
        <h2>{t('udEarnestReport')}</h2>
        <span style={{ fontSize: '0.85rem', fontWeight: 800, color: '#f59e0b', direction: 'ltr' }}>
          {fmt(earnest.total)} SAR
        </span>
      </div>
      <div className="data-table-wrapper">
        <table className="data-table" style={{ fontSize: '0.78rem' }}>
          <thead>
            <tr>
              <th>{t('udProject')}</th>
              <th>{t('udUnit')}</th>
              <th>{t('udClient')}</th>
              <th>{t('crmPhone') || (lang === 'ar' ? 'الجوال' : 'Phone')}</th>
              <th>{t('udEarnest')}</th>
              <th>{t('udPayment')}</th>
              <th>{t('udStatus')}</th>
            </tr>
          </thead>
          <tbody>
            {earnest.entries.map((e, i) => (
              <tr key={i}>
                <td style={{ fontWeight: 700 }}>{projLabel(e.project, t)}</td>
                <td style={{ fontWeight: 700 }}>{e.unit_code}</td>
                <td>{e.client_name}</td>
                <td style={{ direction: 'ltr' }}>{e.client_phone}</td>
                <td style={{ direction: 'ltr' }}>{fmt(e.earnest_money)}</td>
                <td>{e.payment_method}</td>
                <td>
                  <span
                    style={{
                      padding: '2px 8px',
                      borderRadius: 10,
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      background: (ST_COLORS[e.status] || '#888') + '18',
                      color: ST_COLORS[e.status] || '#888',
                    }}
                  >
                    {stLabel(e.status, lang)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ReportsTab;
